import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSettlement } from '../../../contexts/SettlementContext';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import type { SettlementChecklistItem } from '../../../utils/settlementFlowConfig';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';

const PHASE = 'probate';

const EMPTY_CHECKLIST: SettlementChecklistItem[] = [];

type StepStatus = 'not-started' | 'in-progress' | 'complete';

export function useProbateStep(stepId: string, fieldKeysCsv: string) {
  const formFieldKeys = useMemo(() => fieldKeysCsv.split(',').filter(Boolean), [fieldKeysCsv]);

  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const { isArchived } = useSettlement();

  const {
    isLoading,
    error,
    loadPhaseProgress,
    updateStepProgress,
    toggleChecklistItem,
    loadFrameData,
    saveFrameData,
  } = useSettlementSession(estateId, { readOnly: isArchived });

  const stepDef = SETTLEMENT_PHASE_CONFIGS.probate.steps.find((s) => s.id === stepId);
  const checklistDefs = useMemo(() => {
    const items = stepDef?.subTasks?.[0]?.checklistItems;
    return items && items.length > 0 ? items : EMPTY_CHECKLIST;
  }, [stepDef]);
  const checklistIds = useMemo(() => checklistDefs.map((c) => c.id), [checklistDefs]);

  const [status, setStatus] = useState<StepStatus>('not-started');
  const [progressReady, setProgressReady] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const formLoadedRef = useRef(false);
  const formFrameId = `probate-step-forms-${stepId}`;

  useEffect(() => {
    const defaults: Record<string, string> = {};
    for (const k of formFieldKeys) defaults[k] = '';
    if (!estateId) {
      setFormValues(defaults);
      formLoadedRef.current = false;
      return;
    }
    let cancelled = false;
    formLoadedRef.current = false;
    (async () => {
      const fd = await loadFrameData(formFrameId);
      const saved = (fd?.data ?? {}) as Record<string, unknown>;
      const merged = { ...defaults };
      for (const k of formFieldKeys) {
        const v = saved[k];
        if (v != null && v !== '') merged[k] = String(v);
      }
      if (!cancelled) {
        setFormValues(merged);
        formLoadedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, formFrameId, loadFrameData, formFieldKeys]);

  useEffect(() => {
    if (!estateId || isArchived || !formLoadedRef.current || formFieldKeys.length === 0) return;
    const t = setTimeout(() => {
      void saveFrameData(formFrameId, formValues);
    }, 500);
    return () => clearTimeout(t);
  }, [formValues, estateId, isArchived, formFrameId, saveFrameData, formFieldKeys.length]);

  useEffect(() => {
    if (!estateId) {
      setStatus('not-started');
      setCheckedIds(new Set());
      setProgressReady(false);
      return;
    }
    setProgressReady(false);
    let cancelled = false;
    (async () => {
      const steps = await loadPhaseProgress(PHASE);
      if (cancelled) return;
      const row = steps.find((s) => s.stepId === stepId);
      setStatus((row?.status as StepStatus) || 'not-started');
      setCheckedIds(new Set(row?.completedChecklistItems ?? []));
      setProgressReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, stepId, loadPhaseProgress]);

  const checklistCompleted = checklistIds.filter((id) => checkedIds.has(id)).length;
  const checklistTotal = checklistIds.length;

  const ensureInProgress = useCallback(async () => {
    if (status === 'not-started' && estateId) {
      setStatus('in-progress');
      await updateStepProgress(stepId, PHASE, { status: 'in-progress' });
    }
  }, [status, estateId, stepId, updateStepProgress]);

  const toggleItem = useCallback(
    async (itemId: string, next: boolean) => {
      await ensureInProgress();
      setCheckedIds((prev) => {
        const n = new Set(prev);
        if (next) n.add(itemId);
        else n.delete(itemId);
        return n;
      });
      if (estateId) {
        await toggleChecklistItem(stepId, PHASE, itemId, next);
      }
    },
    [ensureInProgress, estateId, stepId, toggleChecklistItem]
  );

  useEffect(() => {
    if (!estateId || !progressReady || checklistTotal === 0) return;

    const allDone = checklistIds.every((id) => checkedIds.has(id));
    if (allDone && status !== 'complete') {
      setStatus('complete');
      void updateStepProgress(stepId, PHASE, { status: 'complete' });
      return;
    }
    if (!allDone && status === 'complete') {
      setStatus('in-progress');
      void updateStepProgress(stepId, PHASE, { status: 'in-progress' });
    }
  }, [
    checkedIds,
    checklistIds,
    checklistTotal,
    estateId,
    progressReady,
    status,
    stepId,
    updateStepProgress,
  ]);

  const setField = useCallback(
    (key: string, value: string) => {
      void ensureInProgress();
      setFormValues((p) => ({ ...p, [key]: value }));
    },
    [ensureInProgress]
  );

  const g = stepDef?.guidance;

  return {
    estateId,
    isArchived,
    isLoading,
    error,
    stepDef,
    g,
    checklistDefs,
    checklistIds,
    checklistCompleted,
    checklistTotal,
    status,
    checkedIds,
    toggleItem,
    formValues,
    setField,
    formFrameId,
    touchActivity: ensureInProgress,
  };
}
